import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Tabs, Tab, Box } from '@mui/material';
import { SipInterworkingTab } from './SipInterworkingPage';
import AdminShortcutsPage from './AdminShortcutsPage';
import SipUsersTab from '../../components/admin/tenant/SipUsersTab';
import PstnTab from '../../components/admin/tenant/PstnTab';
import UserManagment from '../UserManagment';
import SitesListPage from './SitesListPage';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { getTenants, addTenant, updateTenant, getTenantDetails, getTenantSmsConfigAdmin, updateTenantSmsConfigAdmin, testSendSms, getTenantVoiceNumbers, setTenantVoiceNumberDefault } from '../../api/backendApi';
import TenantDeletionDialog, { canPermanentlyDeleteTenant } from './TenantDeletionDialog';
import { tenantDetailsHref } from './supportNav';
import { readDealerAdminSession } from './dealerAdminSession';
import { readSupportedTenantSession, subscribeSupportedTenantSession } from './supportedTenantsSession';
import { suggestSipDomain } from './sipDomainName';
import { tenantsVisibleForSession } from './tenantManagementScope';
import { formatE164Display, numberOptionLabel } from '../../components/admin/tenant/PhoneNumbersTab';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorPanel from '../../components/common/ErrorPanel';
import Button from '../../components/common/Button';
import MainLayout from '../../components/layout/MainLayout';

const SMS_A2P_BLOCKED = new Set(['+13648884343']);

/**
 * TenantManagementPage component
 *
 * Allows administrators to:
 * - View a list of all tenants
 * - Add new tenants
 * - Edit existing tenants
 * - Permanently delete a tenant after export (Superadmin only)
 * - View and reassign detached users
 */
/** Edit Tenant card fills leftover viewport; tabs stay put; all tab bodies scroll. */
const EDIT_TENANT_PANEL_SX = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  bgcolor: '#fff',
  border: '1px solid #e5e7eb',
  borderTop: '2px solid #3b82f6',
  borderRadius: 1,
  mt: 2,
};

const EDIT_TENANT_TAB_SCROLL_SX = {
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  px: 3,
  pb: 4,
};

const TenantManagementPage = () => {
  // Authentication & access control
  const { userData, userClaims, isAdmin } = useAuth();
  const userRole = userClaims?.role || userData?.role;
  const tenantIdFromAuth = userClaims?.tenantId || userData?.tenantId;
  const isTenantAdmin = userRole === 'tenant_admin';
  const isTenantAdminView = isTenantAdmin && !isAdmin;
  const isSuperAdmin = userRole === 'superadmin';

  // State management
  const [tenants, setTenants] = useState([]);
  const [filteredTenants, setFilteredTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterValue, setFilterValue] = useState('');

  // Details state for edit modal
  const [selectedTenantDetails, setSelectedTenantDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState(null);

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeEditTab, setActiveEditTab] = useState('settings');
  // SMS Config state (null until fetched)
  const [smsConfig, setSmsConfig] = useState(null);
  const [loadingSms, setLoadingSms] = useState(false);
  const [smsError, setSmsError] = useState(null);
  const [showTestSmsModal, setShowTestSmsModal] = useState(false);
  const [testSmsTo, setTestSmsTo] = useState('');
  const [testSmsMessage, setTestSmsMessage] = useState('Test message from Admin');
  const [hasAutoOpenedForTenantAdmin, setHasAutoOpenedForTenantAdmin] = useState(false);
  const [supportTenant, setSupportTenant] = useState(() => (
    readDealerAdminSession() ? null : readSupportedTenantSession()
  ));
  const [smsDefaultNumber, setSmsDefaultNumber] = useState(null);
  const [smsHasCapableNumbers, setSmsHasCapableNumbers] = useState(false);
  const [smsNumbers, setSmsNumbers] = useState([]);
  const [smsDefaultNumberId, setSmsDefaultNumberId] = useState(null);

  // Render header section
  const renderHeader = () => (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 mt-2 gap-2">
      <h1 className="text-2xl font-bold text-gray-800 mb-2 sm:mb-0">Tenant Management</h1>
    </div>
  );

  // Form values
  const [newTenantName, setNewTenantName] = useState('');
  const [enableSip, setEnableSip] = useState(false);
  const [pendingTenantId, setPendingTenantId] = useState('');
  const [sipDomainName, setSipDomainName] = useState('');
  const [sipDomainEdited, setSipDomainEdited] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);

  useEffect(() => subscribeSupportedTenantSession(() => {
    setSupportTenant(readDealerAdminSession() ? null : readSupportedTenantSession());
    setHasAutoOpenedForTenantAdmin(false);
  }), []);

  // Fetch tenants on component mount and when the supported tenant changes.
  useEffect(() => {
    fetchTenants();
  }, [isSuperAdmin, supportTenant?.tenantId]);

  // Apply filter when tenant list, search, or edit selection changes.
  // While editing, superadmin/admin only see the tenant being edited in the list.
  useEffect(() => {
    const editingId =
      showEditModal && selectedTenant
        ? selectedTenant.id || selectedTenant.tenantId
        : null;

    if (editingId) {
      setFilteredTenants(
        tenants.filter((tenant) => (tenant.id || tenant.tenantId) === editingId)
      );
      return;
    }

    if (filterValue) {
      const filtered = tenants.filter(
        (tenant) =>
          (tenant.id || '').toLowerCase().includes(filterValue.toLowerCase()) ||
          (tenant.name && tenant.name.toLowerCase().includes(filterValue.toLowerCase()))
      );
      setFilteredTenants(filtered);
    } else {
      setFilteredTenants(tenants);
    }
  }, [tenants, filterValue, showEditModal, selectedTenant]);

  // Fetch all tenants from the API
  const fetchTenants = async () => {
    try {
      setLoading(true);
      const tenantsData = await getTenants({ includeInactive: isSuperAdmin });
      const allTenants = Array.isArray(tenantsData) ? tenantsData : [];

      const visibleTenants = tenantsVisibleForSession(allTenants, {
        isTenantAdmin,
        tenantIdFromAuth,
        supportTenant: readDealerAdminSession() ? null : readSupportedTenantSession(),
      });

      setTenants(visibleTenants);
      setFilteredTenants(visibleTenants);
      setError(null);
    } catch (err) {
      console.error('Error fetching tenants:', err);
      const session = readDealerAdminSession() ? null : readSupportedTenantSession();
      if (session?.tenantId) {
        const visibleTenants = tenantsVisibleForSession([], { supportTenant: session });
        setTenants(visibleTenants);
        setFilteredTenants(visibleTenants);
        setError(null);
      } else {
        setError('Failed to load tenants. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch SMS config for a specific tenant (admin)
  const loadTenantSmsConfig = async (tenantId, { force = false } = {}) => {
    if (!tenantId) return;
    try {
      setLoadingSms(true);
      setSmsError(null);
      const resp = await getTenantSmsConfigAdmin(tenantId, { force });
      const cfg = resp?.data || resp || {};
      setSmsConfig(cfg);
    } catch (e) {
      console.warn('SMS config endpoint unavailable or failed; using defaults', e);
      // Do not block UI on missing/failed SMS config; use defaults
      setSmsError(null);
      setSmsConfig({ __unsupported: true });
    } finally {
      setLoadingSms(false);
    }
  };

  const loadTenantSmsDefaults = async (tenantId) => {
    if (!tenantId) return;
    try {
      const resp = await getTenantVoiceNumbers(tenantId);
      const list = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
      let hasCapable = false;
      let defaultNumber = null;
      let defaultId = null;
      const smsList = [];

      for (const row of list) {
        const caps = (row && row.capabilities) || {};
        const hasSms = !!caps.sms;
        const e164 = row.e164 || '';
        if (hasSms && !SMS_A2P_BLOCKED.has(e164) && row.status !== 'suspended' && row.status !== 'disabled') {
          hasCapable = true;
          smsList.push({
            id: row.id,
            e164,
            smsShareMode: String(row.smsShareMode || 'shared').toLowerCase(),
            label: numberOptionLabel(e164, row.label),
          });
          const defaults = Array.isArray(row && row.defaultFor) ? row.defaultFor : [];
          if (!defaultNumber && defaults.includes('sms')) {
            defaultNumber = e164 || null;
            defaultId = row.id || null;
          }
        }
      }

      setSmsHasCapableNumbers(hasCapable);
      setSmsDefaultNumber(defaultNumber);
      setSmsDefaultNumberId(defaultId);
      setSmsNumbers(smsList);
    } catch (e) {
      setSmsHasCapableNumbers(false);
      setSmsDefaultNumber(null);
      setSmsDefaultNumberId(null);
      setSmsNumbers([]);
    }
  };

  const handleChangeSmsDefault = async (e164) => {
    if (!isSuperAdmin || !selectedTenant?.id) return;
    setSmsConfig((prev) => ({ ...(prev || {}), fromE164: e164 || null }));
  };

  const handlePhoneNumbersChanged = () => {
    if (selectedTenant?.id) {
      loadTenantSmsDefaults(selectedTenant.id);
    }
  };

  // Save SMS config (only allowed fields)
  const handleSaveSmsConfig = async () => {
    if (!isSuperAdmin || !selectedTenant?.id || !smsConfig) return;
    if (smsConfig.__unsupported) {
      toast.info('SMS settings API is not available in this environment yet.');
      return;
    }
    try {
      setLoadingSms(true);
      const mode = smsConfig.mode || 'none';
      const payload = {
        mode,
        status: smsConfig.status || 'active',
        fromE164: mode === 'none' ? null : (smsConfig.fromE164 || null),
      };
      await updateTenantSmsConfigAdmin(selectedTenant.id, payload);
      toast.success('SMS settings updated');
      await loadTenantSmsConfig(selectedTenant.id);
      await loadTenantSmsDefaults(selectedTenant.id);
    } catch (e) {
      console.error('Failed to update SMS config:', e);
      if (e?.code === 'UNSUPPORTED' || String(e?.message || '').includes('not available')) {
        toast.info('SMS settings API is not available in this environment yet.');
      } else {
        toast.error(e?.message || 'Failed to update SMS settings');
      }
    } finally {
      setLoadingSms(false);
    }
  };

  // Compute outbound caller ID preview
  const getOutboundPreview = () => {
    const cfg = smsConfig || {};
    const mode = cfg.mode || 'none';
    if (mode === 'none' || cfg.status === 'disabled') {
      return 'SMS disabled (mode=none or status=disabled)';
    }
    const from = cfg.fromE164 || cfg.dedicatedNumber || smsDefaultNumber;
    if (from) return `From: ${formatE164Display(from)} (${mode})`;
    return `${mode} (no number selected)`;
  };

  const handleSmsModeChange = (nextMode) => {
    if (!isSuperAdmin) return;
    setSmsConfig((prev) => {
      const next = { ...(prev || {}), mode: nextMode };
      if (nextMode === 'none') {
        next.fromE164 = null;
      } else if (!next.fromE164) {
        const match = smsNumbers.find((n) => n.smsShareMode === nextMode);
        next.fromE164 = match?.e164 || smsNumbers[0]?.e164 || null;
      }
      return next;
    });
  };

  // Send Test SMS
  const handleSendTestSms = async () => {
    if (!isSuperAdmin || !selectedTenant?.id) return;
    if (!testSmsTo || !testSmsMessage) {
      toast.error('Enter To and Message');
      return;
    }
    try {
      setLoadingSms(true);
      const res = await testSendSms({ tenantId: selectedTenant.id, to: testSmsTo, message: testSmsMessage });
      if (res?.success) {
        toast.success('Test SMS sent');
      } else {
        toast.warn(`Test SMS result: ${res?.message || 'Check logs'}`);
      }
      setShowTestSmsModal(false);
    } catch (e) {
      console.error('Test SMS failed:', e);
      toast.error('Failed to send test SMS');
    } finally {
      setLoadingSms(false);
    }
  };

  const openAddTenant = () => {
    setPendingTenantId(crypto.randomUUID().replace(/-/g, ''));
    setNewTenantName('');
    setEnableSip(false);
    setSipDomainName('');
    setSipDomainEdited(false);
    setShowAddModal(true);
  };

  useEffect(() => {
    if (!showAddModal || !enableSip || !pendingTenantId || sipDomainEdited) return undefined;
    let cancel = false;
    suggestSipDomain(newTenantName || 'tenant', pendingTenantId).then((domain) => {
      if (!cancel) setSipDomainName(domain);
    });
    return () => { cancel = true; };
  }, [showAddModal, enableSip, newTenantName, pendingTenantId, sipDomainEdited]);

  // Handle adding a new tenant
  const handleAddTenant = async () => {
    if (!newTenantName.trim()) {
      toast.error('Tenant name cannot be empty');
      return;
    }

    try {
      setLoading(true);
      const created = await addTenant({
        id: pendingTenantId,
        name: newTenantName.trim(),
        enableSip,
        ...(enableSip ? { sipDomainName } : {}),
      });
      const sipSetup = created?.data?.sipSetup;
      if (sipSetup?.message || (sipSetup && sipSetup.status !== 'configured')) {
        toast.warn(sipSetup?.message || 'Tenant created; SIP setup incomplete.');
      } else if (enableSip && sipSetup?.status === 'configured') {
        toast.success('Tenant added. SIP is configured. A phone is not registered until you add a SIP user.');
      } else {
        toast.success('Tenant added successfully');
      }
      setShowAddModal(false);
      setNewTenantName('');
      setEnableSip(false);
      fetchTenants();
    } catch (err) {
      toast.error('Failed to add tenant');
      console.error('Error adding tenant:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle updating an existing tenant
  const handleUpdateTenant = async () => {
    if (!isSuperAdmin) return;
    if (!selectedTenant || !selectedTenant.name.trim()) {
      toast.error('Tenant name cannot be empty');
      return;
    }

    try {
      setLoading(true);
      await updateTenant(selectedTenant.id, { name: selectedTenant.name.trim() });
      toast.success('Tenant updated successfully');
      setShowEditModal(false);
      fetchTenants();
    } catch (err) {
      toast.error('Failed to update tenant');
      console.error('Error updating tenant:', err);
    } finally {
      setLoading(false);
    }
  };

  // Open edit modal with selected tenant
  const openEditModal = tenant => {
    setSelectedTenant({ ...tenant });
    setActiveEditTab(isSuperAdmin ? 'settings' : 'pstn');
    setShowEditModal(true);
    // Load tenant details non-blocking
    loadTenantDetails(tenant.id);
    // Kick off SMS config load immediately (force bypass cache)
    loadTenantSmsConfig(tenant.id, { force: true });
    loadTenantSmsDefaults(tenant.id);
  };

  // Fetch details for a specific tenant
  const loadTenantDetails = async tenantId => {
    if (!tenantId) return;
    try {
      setLoadingDetails(true);
      setDetailsError(null);
      const details = await getTenantDetails(tenantId);
      setSelectedTenantDetails(details);
    } catch (e) {
      console.error('Error loading tenant details:', e);
      setDetailsError('Failed to load tenant details.');
    } finally {
      setLoadingDetails(false);
    }
  };

  // Open delete confirmation modal with selected tenant
  const openDeleteModal = tenant => {
    setSelectedTenant(tenant);
    setShowDeleteModal(true);
  };

  // Open the supported tenant, or a tenant admin's own tenant.
  useEffect(() => {
    if (hasAutoOpenedForTenantAdmin) return;
    if (!tenants || tenants.length === 0) return;

    const supportId = supportTenant?.tenantId;
    if (supportId) {
      const target = tenants.find(t => (t.id || t.tenantId) === supportId);
      if (!target) return;
      openEditModal(target);
      setHasAutoOpenedForTenantAdmin(true);
      return;
    }

    if (isAdmin) return;
    if (!isTenantAdmin || !tenantIdFromAuth) return;

    const ownTenant = tenants.find(t => {
      const id = t.id || t.tenantId;
      return id === tenantIdFromAuth;
    });

    if (!ownTenant) {
      setHasAutoOpenedForTenantAdmin(true);
      return;
    }

    openEditModal(ownTenant);
    setHasAutoOpenedForTenantAdmin(true);
  }, [isAdmin, isTenantAdmin, tenantIdFromAuth, tenants, hasAutoOpenedForTenantAdmin, supportTenant?.tenantId]);

  useEffect(() => {
    if (!isSuperAdmin && activeEditTab === 'settings') {
      setActiveEditTab('pstn');
    }
  }, [isSuperAdmin, activeEditTab]);

  // If not an admin or tenant_admin, prevent access
  if (!isAdmin && !isTenantAdmin) {
    return (
      <ErrorPanel
        title="Access Denied"
        message="You must be an administrator to manage tenants."
        variant="error"
      />
    );
  }

  return (
    <>
      <MainLayout>
        <Box
          sx={{
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            maxWidth: '100%',
            mx: 'auto',
            px: { xs: 2, md: 3 },
            pt: 1,
            pb: 2,
            boxSizing: 'border-box',
          }}
        >
        {renderHeader()}
        {!isTenantAdminView && !(showEditModal && selectedTenant) && (
        <div className="mb-6 flex justify-between items-center">
          {isSuperAdmin && (
          <div style={{ position: 'relative', width: '100%', maxWidth: 400 }}>
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg
                className="w-4 h-4 text-gray-500"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 20"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                />
              </svg>
            </div>
            <input
              id="tenant-filter"
              type="text"
              value={filterValue}
              onChange={e => setFilterValue(e.target.value)}
              placeholder="Filter by ID or name..."
              className="block w-full p-3 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-white focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          )}
          {isAdmin && (
            <div className="ml-auto">
              <Button onClick={openAddTenant}>
                Add New Tenant
              </Button>
            </div>
          )}
        </div>
        )}

        {isTenantAdminView && (!showEditModal || !selectedTenant) && (
          <div className="flex justify-center p-10">
            {loading || !hasAutoOpenedForTenantAdmin ? (
              <LoadingSpinner />
            ) : error ? (
              <ErrorPanel title="Error" message={error} variant="error" />
            ) : (
              <ErrorPanel
                title="Tenant not found"
                message="Your tenant record could not be loaded. Please try again or contact support."
                variant="error"
              />
            )}
          </div>
        )}

          {/* Tenants Table — superadmin/admin only */}
          {!isTenantAdminView && (
          <Box
            className="rounded-lg border border-gray-200 shadow"
            sx={{
              flex: showEditModal && selectedTenant ? '0 1 auto' : 1,
              minHeight: 0,
              maxHeight: showEditModal && selectedTenant ? '28vh' : '100%',
              overflow: 'auto',
            }}
          >
            {loading && !tenants.length ? (
              <div className="flex justify-center p-10">
                <LoadingSpinner />
              </div>
            ) : error ? (
              <ErrorPanel title="Error" message={error} variant="error" />
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Tenant Name
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Tenant ID
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Status
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTenants.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center text-sm text-gray-500">
                        <div className="flex flex-col items-center justify-center space-y-2">
                          <svg
                            className="w-12 h-12 text-gray-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                          </svg>
                          <span className="font-medium">
                            {tenants.length === 0 ? 'No tenants found' : 'No matching tenants'}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredTenants.map(tenant => {
                      const tenantRowId = tenant.id || tenant.tenantId;
                      const canEditTenant = isAdmin || (isTenantAdmin && tenantIdFromAuth && tenantRowId === tenantIdFromAuth);
                      const canDeleteTenant = canPermanentlyDeleteTenant(userRole) && tenant.tombstone !== true && tenant.deletionStatus !== 'deleted';
                      const deleteLabel = tenant.deletionLock || tenant.deletionStatus === 'deleting' ? 'Continue deletion' : 'Delete';
                      const statusLabel = tenant.tombstone || tenant.deletionStatus === 'deleted'
                        ? 'Deleted'
                        : tenant.deletionLock || tenant.deletionStatus === 'deleting'
                          ? 'Deleting'
                          : tenant.active === false
                            ? 'Inactive'
                            : 'Active';
                      const statusClass = statusLabel === 'Active'
                        ? 'bg-green-100 text-green-800'
                        : statusLabel === 'Deleting'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-gray-100 text-gray-700';
                      const isEditing = selectedTenant && (selectedTenant.id === tenantRowId || selectedTenant.tenantId === tenantRowId);
                      return (
                      <React.Fragment key={tenantRowId}>
                      <tr className={`hover:bg-gray-50 transition duration-150 ${isEditing ? 'bg-blue-50' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                              <span className="text-blue-800 text-lg font-semibold">
                                {(tenant.name || '?')[0].toUpperCase()}
                              </span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {tenant.name || 'Unnamed Tenant'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {tenant.id || tenant.tenantId}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusClass}`}>
                            {statusLabel}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {isSuperAdmin && (
                            <Link
                              to={tenantDetailsHref(tenantRowId)}
                              className="text-blue-700 hover:text-blue-900 mr-4"
                              data-testid={`tenant-detail-${tenantRowId}`}
                            >
                              Details
                            </Link>
                          )}
                          {canEditTenant && (
                            <button
                              onClick={() => openEditModal(tenant)}
                              className="text-indigo-600 hover:text-indigo-900 mr-4"
                            >
                              Edit
                            </button>
                          )}
                          {canDeleteTenant && (
                            <button
                              onClick={() => openDeleteModal(tenant)}
                              className="text-red-600 hover:text-red-900"
                              data-testid={`tenant-delete-${tenantRowId}`}
                            >
                              {deleteLabel}
                            </button>
                          )}
                        </td>
                      </tr>
                      </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            )}
          </Box>
          )}

      {/* Add Tenant Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">Add New Tenant</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="tenant-name" className="block text-sm font-medium text-gray-700">
                    Tenant Name
                  </label>
                  <input
                    id="tenant-name"
                    type="text"
                    value={newTenantName}
                    onChange={e => setNewTenantName(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Enter tenant name..."
                  />
                </div>
                <label className="flex items-start gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={enableSip}
                    onChange={(event) => setEnableSip(event.target.checked)}
                  />
                  <span>
                    <span className="font-medium text-gray-900">Enable SIP devices and paging</span>
                    <span className="block text-gray-600">Connect compatible SIP phones, speakers and paging endpoints. You can enable this later.</span>
                  </span>
                </label>
                {enableSip ? (
                  <label className="block text-sm font-medium text-gray-700">
                    SIP domain
                    <input
                      type="text"
                      value={sipDomainName}
                      onChange={(event) => {
                        setSipDomainEdited(true);
                        setSipDomainName(event.target.value.trim().toLowerCase());
                      }}
                      className="mt-1 block w-full rounded-md border-gray-300 font-mono shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </label>
                ) : null}

                <div className="flex justify-end space-x-2">
                  <Button variant="secondary" onClick={() => setShowAddModal(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleAddTenant}
                    disabled={loading || !newTenantName.trim() || (enableSip && !sipDomainName)}
                  >
                    {loading ? 'Adding...' : 'Add Tenant'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Edit Tenant: tabs stay put; every tab body scrolls in the leftover viewport. */}
      {selectedTenant && showEditModal && (
        <Box
          sx={{
            ...EDIT_TENANT_PANEL_SX,
            ...(isTenantAdminView ? { mt: 0 } : {}),
          }}
          data-testid="edit-tenant-panel"
        >
          <Box sx={{ px: 3, pt: 2, flexShrink: 0 }}>
              {!isTenantAdminView && (
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">Edit Tenant</h2>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
              </div>
              )}

              <Tabs
                value={activeEditTab}
                onChange={(e, v) => setActiveEditTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{ borderBottom: '1px solid #e5e7eb', mb: 1 }}
              >
                {isSuperAdmin && <Tab value="settings" label="Settings" />}
                <Tab value="pstn" label="PSTN" />
                <Tab value="sip" label="SIP" />
                <Tab value="sip-users" label="SIP User" />
                {!isTenantAdminView && <Tab value="users" label="Users" />}
                <Tab value="sites" label="Sites" />
                <Tab value="shortcuts" label="Shortcuts" />
              </Tabs>
          </Box>
          <Box sx={EDIT_TENANT_TAB_SCROLL_SX} data-testid="edit-tenant-tab-scroll">

              {isSuperAdmin && activeEditTab === 'settings' && (
              <div className="space-y-4 ca-form">
                {/* Basic Info Row */}
                <div className="ca-form-grid">
                  <div className="ca-field">
                    <label htmlFor="edit-tenant-name" className="ca-label">Tenant Name</label>
                    <input
                      id="edit-tenant-name"
                      type="text"
                      value={selectedTenant.name || ''}
                      onChange={e => setSelectedTenant({ ...selectedTenant, name: e.target.value })}
                      className="ca-input"
                      placeholder="Enter tenant name..."
                      disabled={!isSuperAdmin}
                    />
                    <p className="ca-help">This name is shown in admin pages and helps you identify the tenant.</p>
                  </div>
                  {!isTenantAdminView && (
                  <div className="ca-field">
                    <label className="ca-label">Tenant ID</label>
                    <div className="ca-input bg-gray-50">
                      {selectedTenant.id}
                    </div>
                    <p className="ca-help">Read-only unique identifier used by the backend and agents.</p>
                  </div>
                  )}
                </div>

                {/* SMS Settings Panel */}
                <div className="mt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="ca-label">SMS Settings</label>
                      <p className="ca-help">Choose None, Shared, or Dedicated. Shared/Dedicated use a number you assigned on PSTN → Numbers.</p>
                    </div>
                    {isSuperAdmin && (
                    <div className="flex gap-2">
                      <Button variant="secondary" onClick={() => setShowTestSmsModal(true)}>
                        Test SMS
                      </Button>
                      <Button variant="primary" onClick={handleSaveSmsConfig} disabled={loadingSms || !smsConfig || smsConfig.__unsupported}>
                        {loadingSms ? 'Saving...' : 'Save SMS Settings'}
                      </Button>
                    </div>
                    )}
                  </div>
                  {smsConfig?.__unsupported && (
                    <div className="mt-1 text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-2 py-1">
                      SMS config endpoint is not available in this environment. Defaults are shown; saving is disabled.
                    </div>
                  )}
                  {smsError && (
                    <div className="mt-1 text-sm text-red-600">{smsError}</div>
                  )}
                  <div className="mt-2 ca-form-grid">
                    <div className="ca-field">
                      <label className="ca-label">SMS mode</label>
                      <select
                        value={smsConfig?.mode || 'none'}
                        onChange={e => handleSmsModeChange(e.target.value)}
                        className="ca-select"
                        disabled={!isSuperAdmin || !!smsConfig?.__unsupported}
                      >
                        <option value="none">None — outbound SMS disabled</option>
                        <option value="shared" disabled={!smsHasCapableNumbers}>
                          Shared
                        </option>
                        <option value="dedicated" disabled={!smsHasCapableNumbers}>
                          Dedicated
                        </option>
                      </select>
                      <p className="ca-help">None rejects outbound SMS. Shared/Dedicated require an assigned SMS-enabled number with matching mode.</p>
                    </div>
                    <div className="ca-field">
                      <label className="ca-label">Status</label>
                      <select
                        value={smsConfig?.status || 'active'}
                        onChange={e => setSmsConfig(prev => ({ ...(prev || {}), status: e.target.value }))}
                        className="ca-select"
                        disabled={!isSuperAdmin || !!smsConfig?.__unsupported}
                      >
                        <option value="active">active</option>
                        <option value="disabled">disabled</option>
                      </select>
                      <p className="ca-help">Set to <strong>disabled</strong> to temporarily turn off SMS sending for this tenant.</p>
                    </div>
                    {(smsConfig?.mode || 'none') !== 'none' && (
                    <div className="ca-field ca-col-span-2">
                      <label className="ca-label">SMS From number</label>
                      {smsNumbers.filter((n) => n.smsShareMode === (smsConfig?.mode || 'shared')).length > 0 ? (
                        <select
                          value={smsConfig?.fromE164 || ''}
                          onChange={e => handleChangeSmsDefault(e.target.value)}
                          className="ca-select"
                          disabled={!isSuperAdmin || !!smsConfig?.__unsupported || loadingSms}
                        >
                          <option value="">Select assigned SMS number…</option>
                          {smsNumbers
                            .filter((n) => n.smsShareMode === (smsConfig?.mode || 'shared'))
                            .map(n => (
                              <option key={n.id} value={n.e164}>{n.label}</option>
                            ))}
                        </select>
                      ) : (
                        <div className="ca-input bg-gray-50">
                          No matching SMS {smsConfig?.mode} assignments. Add one on PSTN → Numbers.
                        </div>
                      )}
                      <p className="ca-help">Only active SMS-enabled numbers assigned to this tenant. Stores E.164.</p>
                    </div>
                    )}
                    <div className="ca-field">
                      <label className="ca-label">MessagingServiceSid</label>
                      <div className="ca-input bg-gray-50">
                        {smsConfig?.messagingServiceSid || '—'}
                      </div>
                      <p className="ca-help">Read-only. Reserved for a future Messaging Service option.</p>
                    </div>
                    <div className="ca-field ca-col-span-2">
                      <label className="ca-label">Outbound SMS From preview</label>
                      <div className="ca-input bg-gray-50 border border-gray-200 text-gray-700">
                        {getOutboundPreview()}
                      </div>
                      <p className="ca-help">Shows the number recipients will see based on the current settings.</p>
                    </div>
                  </div>
                </div>

                {isSuperAdmin && (
                <div className="ca-actions">
                  <Button
                    variant="primary"
                    onClick={handleUpdateTenant}
                    disabled={loading || !selectedTenant.name?.trim()}
                  >
                    {loading ? 'Updating...' : 'Update Tenant'}
                  </Button>
                </div>
                )}
                {/* Tenant details panel intentionally hidden for now (not useful in UI) */}
              </div>
              )}
              {activeEditTab === 'pstn' && (
                <Box sx={{ mt: 1 }}>
                  <PstnTab
                    tenantId={selectedTenant?.id}
                    tenantName={selectedTenant?.name || ''}
                    onNumbersChanged={handlePhoneNumbersChanged}
                    canManage={isAdmin}
                    canActivatePstn={isSuperAdmin}
                  />
                </Box>
              )}
              {activeEditTab === 'sip' && (
                <Box sx={{ mt: 1 }}>
                  <SipInterworkingTab
                    tenantId={selectedTenant?.id || selectedTenant?.tenantId || (supportTenant ? '' : tenantIdFromAuth)}
                    tenantName={selectedTenant?.name || supportTenant?.name || ''}
                  />
                </Box>
              )}
              {activeEditTab === 'sip-users' && (
                <SipUsersTab tenantId={selectedTenant?.id || selectedTenant?.tenantId || (supportTenant ? '' : tenantIdFromAuth)} />
              )}
              {!isTenantAdminView && activeEditTab === 'users' && (
                <Box sx={{ mt: 1, mx: { xs: -1, md: -2 } }}>
                  <UserManagment
                    embedded
                    tenantId={selectedTenant?.id}
                    tenantName={selectedTenant?.name}
                    pageTitle="Users"
                  />
                </Box>
              )}
              {activeEditTab === 'sites' && (
                <Box sx={{ mt: 1 }}>
                  <SitesListPage embedded tenantId={selectedTenant?.id} />
                </Box>
              )}
              {activeEditTab === 'shortcuts' && (
                <Box sx={{ mt: 1 }}>
                  <AdminShortcutsPage
                    embedded
                    tenantId={selectedTenant?.id || selectedTenant?.tenantId || (supportTenant ? '' : tenantIdFromAuth)}
                    tenantName={selectedTenant?.name || supportTenant?.name || ''}
                  />
                </Box>
              )}
          </Box>
        </Box>
      )}

      {showDeleteModal && selectedTenant && canPermanentlyDeleteTenant(userRole) && (
        <TenantDeletionDialog
          tenant={selectedTenant}
          onClose={() => setShowDeleteModal(false)}
          onFinished={() => {
            setShowDeleteModal(false);
            fetchTenants();
          }}
        />
      )}
      </Box>
    </MainLayout>
    {showTestSmsModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Send Test SMS</h2>
              <button onClick={() => setShowTestSmsModal(false)} className="text-gray-400 hover:text-gray-500">
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">To Number</label>
                <input
                  type="text"
                  value={testSmsTo}
                  onChange={e => setTestSmsTo(e.target.value)}
                  placeholder="+1XXXXXXXXXX"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Message</label>
                <textarea
                  rows="3"
                  value={testSmsMessage}
                  onChange={e => setTestSmsMessage(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={() => setShowTestSmsModal(false)}>Cancel</Button>
                <Button variant="primary" onClick={handleSendTestSms} disabled={loadingSms || !testSmsTo || !testSmsMessage}>
                  {loadingSms ? 'Sending...' : 'Send SMS'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )}
    
    </>
  );
};

export default TenantManagementPage;
